import { Type } from '@sinclair/typebox';
import { FirstBuyerSchema } from '../first-buyer.js';
import { PremiumMetaSchema } from '../premium.js';
import { SupportedDexSchema } from '../supported-dex.js';
import { SupportedNetworkSchema } from '../supported-network.js';
import { TokenSocailsSchema } from '../token/token-meta-extended.js';
import { PoolAthSchema } from './pool-ath.js';
import { PoolAuditSchema } from './pool-audit.js';
import { PoolCreatorSchema } from './pool-creator.js';
import { PoolFeesSchema } from './pool-fees.js';
import { PoolTokenSchema } from './pool-token.js';
import { CreatorStatsSchema, PoolAllPeriodStatsSchema, PoolExtraDetailsSchema, PoolPriceChartSchema, PoolModelAccountsSchema, PoolWalletGroupSchema, } from './pool.js';
export const PoolTokenSchemaExtended = Type.Object({
    ...PoolTokenSchema.properties,
    socials: Type.Optional(TokenSocailsSchema),
    inTrends: Type.Optional(Type.Boolean()),
    trendingRank: Type.Optional(Type.Number()),
});
export const PoolSchemaExtended = Type.Object({
    provider: SupportedDexSchema,
    network: SupportedNetworkSchema,
    poolAddress: Type.String(),
    base: PoolTokenSchemaExtended,
    quote: PoolTokenSchemaExtended,
    pair: Type.Tuple([Type.String(), Type.String()]),
    priceNative: Type.String({
        description: 'Price of the token in the native currency',
    }),
    priceUsd: Type.String(),
    marketCapUsd: Type.String(),
    liquidityUsd: Type.String(),
    liquidityNative: Type.String(),
    liquidityToken: Type.String(),
    fees: PoolFeesSchema,
    poolFee: Type.Optional(Type.Number()),
    periodStats: PoolAllPeriodStatsSchema,
    premiumInfo: PremiumMetaSchema,
    audit: PoolAuditSchema,
    /**
     * Who bought first, in order, and what they are.
     *
     * Three states, and they differ: the list, present and **empty with
     * `firstBuyersMeta` beside it** when a feed knows the numbers but will
     * not spend six kilobytes a card listing the wallets, and **absent** when
     * nobody asked at all. A reader must not treat the third as the second.
     */
    firstBuyers: Type.Optional(Type.Array(FirstBuyerSchema)),
    /**
     * How many bought first and how many of those sniped.
     *
     * A list row shows the two numbers and nothing else, and the wallets
     * themselves are six kilobytes of the thirteen a card weighs — half of
     * what an explore page sends, for a badge that reads "3/70". The feeds
     * send this and leave the array empty; a card read on its own carries
     * both.
     */
    firstBuyersMeta: Type.Optional(Type.Object({ total: Type.Number(), snipers: Type.Number() })),
    holders: Type.Object({
        count: Type.String(),
        top10Holding: Type.String(),
    }),
    /**
     * Who got in at launch, and what they still hold.
     *
     * Optional, and the distinction matters: **absent means nobody asked**,
     * where a zero would mean nobody did. Reading them is most of what a card
     * costs — a pool's first buyers are fetched and then every one of them is
     * asked for its balance and its totals — so a list that shows none of
     * this asks for none of it and these three do not travel.
     *
     * A reader that merges cards into one model must leave an absent group
     * alone rather than write it as zero, or a cheap card will quietly empty
     * the badges of a pool somebody is looking at.
     */
    insiders: Type.Optional(PoolWalletGroupSchema),
    snipers: Type.Optional(PoolWalletGroupSchema),
    bundlers: Type.Optional(PoolWalletGroupSchema),
    priceChart: PoolPriceChartSchema,
    initTimestamp: Type.Optional(Type.String()),
    initBlock: Type.Optional(Type.Number()),
    initTxSignature: Type.Optional(Type.String()),
    initTxCreator: Type.Optional(PoolCreatorSchema),
    extraDetails: Type.Optional(PoolExtraDetailsSchema),
    accounts: Type.Optional(PoolModelAccountsSchema),
    allTimeHigh: PoolAthSchema,
    creatorStats: Type.Optional(CreatorStatsSchema),
    updatedAt: Type.String(),
    infoUpdatedAt: Type.Optional(Type.String()),
    metricsUpdatedAt: Type.Optional(Type.String()),
    chartsUpdatedAt: Type.Optional(Type.String()),
    migrated: Type.Optional(Type.Boolean()),
}, {
    title: 'PairSchemaExtended',
});
export const POOL_INFO_STATIC_FIELDS = [
    'provider',
    'base',
    'quote',
    'pair',
    'initTimestamp',
    'initBlock',
    'initTxSignature',
    'extraDetails',
    'accounts',
];
export const POOL_PREMIUM_FIELDS = [
    'premiumInfo',
];
export const POOL_MIGRATION_FIELDS = [
    'migrated',
];
export const POOL_METRICS_FIELDS = [
    'priceNative',
    'priceUsd',
    'marketCapUsd',
    'liquidityUsd',
    'liquidityNative',
    'liquidityToken',
    'fees',
    'poolFee',
    'periodStats',
    'audit',
    'firstBuyers',
    'holders',
    'insiders',
    'snipers',
    'bundlers',
    'initTxCreator',
    'allTimeHigh',
    'creatorStats',
];
export const POOL_CHARTS_FIELDS = [
    'priceChart',
];
export const VlmmPoolSocialInfoSchema = Type.Object({
    description: Type.String(),
    socials: Type.Object({
        website: Type.String(),
        twitter: Type.String(),
        telegram: Type.String(),
    }),
    ownerBuyAmount: Type.Number(),
    creationHash: Type.String(),
});
export const VlmmPoolSchemaExtended = Type.Object({
    base: PoolTokenSchemaExtended,
    quote: PoolTokenSchemaExtended,
    pair: Type.Tuple([Type.String(), Type.String()]),
    priceNative: Type.String({
        description: 'Price of the token in the native currency',
    }),
    priceUsd: Type.String(),
    marketCapUsd: Type.String(),
    liquidityUsd: Type.String(),
    liquidityNative: Type.String(),
    fees: PoolFeesSchema,
    poolFee: Type.Optional(Type.Number()),
    periodStats: PoolAllPeriodStatsSchema,
    premiumInfo: PremiumMetaSchema,
    audit: PoolAuditSchema,
    firstBuyers: Type.Array(FirstBuyerSchema),
    holders: Type.Object({
        count: Type.String(),
        top10Holding: Type.String(),
    }),
    insiders: PoolWalletGroupSchema,
    snipers: PoolWalletGroupSchema,
    bundlers: PoolWalletGroupSchema,
    socialTokenInfo: VlmmPoolSocialInfoSchema,
    priceChart: PoolPriceChartSchema,
}, {
    title: 'PairSchemaExtended',
});
//# sourceMappingURL=pool-extended.js.map